import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { roleLabel } from "@/lib/roleLabels";
import {
  PaperPlaneTiltIcon,
  CheckCircleIcon,
  XCircleIcon,
  UsersFourIcon,
} from "@phosphor-icons/react";

// Creates each account directly (add-team-member Edge Function) with the
// chosen role and emails the person a "sign in with Microsoft" link via
// Microsoft Graph. There is no password: sign-in is Microsoft SSO only, and
// their first Microsoft sign-in attaches to this pre-created account (same
// email), so the role chosen here is what they get. Replaces the old
// /invites/bulk path, which never worked (the invites table's real shape never
// matched what that generic handler sent — see Audit/AUDIT_REPORT.md's
// invite-pipeline finding).
export default function InviteEmployeesModal({ open, onOpenChange, onInvited, currentUserRole }) {
  const [emails, setEmails] = useState("");
  const [role, setRole] = useState("member");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const canAddAdmin = currentUserRole === "admin";

  const emailCount = emails
    .split(/[,;\s]+/)
    .map((e) => e.trim())
    .filter(Boolean).length;

  const submit = async () => {
    const list = [...new Set(emails.split(/[,;\s]+/).map((e) => e.trim().toLowerCase()).filter(Boolean))];
    if (list.length === 0) { toast.error("Paste at least one email"); return; }
    setLoading(true);
    const added = [], failed = [];
    try {
      // A backgrounded/inactive tab can leave the client holding a stale
      // access token (auto-refresh is throttled while the tab isn't
      // focused) — getSession() transparently refreshes it if needed, so
      // the Edge Function's own caller check below doesn't reject a
      // perfectly legitimate admin session as "Session not found".
      await supabase.auth.getSession();

      for (const email of list) {
        const { data, error } = await supabase.functions.invoke("add-team-member", {
          body: { email, role },
        });
        if (error || data?.error) {
          // supabase-js only gives a generic "Edge Function returned a
          // non-2xx status code" in `error.message` — the actual reason
          // (unauthorized / already_exists / forbidden / the real thrown
          // message) is in the response body, reachable via error.context.
          let reason = data?.error;
          if (!reason && error?.context) {
            try { reason = (await error.context.clone().json())?.error; } catch { /* not JSON */ }
          }
          failed.push({ email, reason: reason || error?.message || "unknown_error" });
        } else if (data?.email_sent === false) {
          // The account was genuinely created — this belongs in "added" (so
          // it counts toward refreshing the Team table below), just flagged
          // so the admin knows to tell the person themselves.
          added.push({
            email,
            role: data?.role || role,
            emailSent: false,
            emailError: data.email_error,
          });
        } else {
          added.push({ email, role: data?.role || role, emailSent: true });
        }
      }
      setResult({ added, failed });
      toast.success(`${added.length} added · ${failed.length} failed`);
      // Fires whenever at least one account now exists, regardless of
      // whether its email went out — that's what the Team table needs to
      // know to refresh itself.
      if (added.length > 0 && onInvited) onInvited({ added, failed });
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setEmails(""); setResult(null); setRole("member");
  };

  // Cancel/Done below call this directly rather than the raw onOpenChange
  // prop — calling that prop straight bypassed the Dialog's own
  // onOpenChange wrapper (which is what actually runs reset()), so `result`
  // stayed populated and the very next open of this modal jumped straight
  // back to the previous results screen instead of a fresh form.
  const close = () => { reset(); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-2xl" data-testid="invite-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <UsersFourIcon size={22} weight="fill" color="#0176D3" /> Add team members
          </DialogTitle>
        </DialogHeader>

        {!result ? (
          <div className="space-y-5">
            <div>
              <Label className="trk-uppercase-label mb-2 block">Emails</Label>
              <Textarea
                data-testid="invite-emails-input"
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                placeholder="firstname.lastname@indorama.com"
                rows={6}
                className="rounded-md font-mono text-sm"
              />
              <div className="flex items-center justify-between mt-2 text-xs text-[#475569]">
                <span>Paste emails separated by newlines, commas or spaces.</span>
                <span className="font-mono">{emailCount} address{emailCount === 1 ? "" : "es"}</span>
              </div>
              <div className="text-xs text-[#94A3B8] mt-1">
                Only <span className="font-mono text-[#0176D3]">@indorama.com</span>,{" "}
                <span className="font-mono text-[#0176D3]">@ng.indorama.com</span> and{" "}
                <span className="font-mono text-[#0176D3]">@sn.indorama.com</span> are allowed.
                Anything else is rejected server-side.
              </div>
            </div>

            <div>
              <Label className="trk-uppercase-label mb-2 block">Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="invite-role-select" className="h-10 rounded-md max-w-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">{roleLabel("member")}</SelectItem>
                  <SelectItem value="manager">{roleLabel("manager")}</SelectItem>
                  {canAddAdmin && <SelectItem value="admin">{roleLabel("admin")}</SelectItem>}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#94A3B8] mt-1.5">
                Each person gets an account with this role and an email inviting them to sign in with their Microsoft account. Existing accounts are left unchanged.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={close} data-testid="invite-cancel-btn">Cancel</Button>
              <Button onClick={submit} disabled={loading || emailCount === 0} data-testid="invite-submit-btn"
                      className="bg-[#0176D3] hover:bg-[#005FB2] text-white">
                <PaperPlaneTiltIcon size={16} weight="fill" className="mr-2" />
                {loading
                  ? "Sending…"
                  : `Add & send invite${emailCount === 1 ? "" : "s"}${emailCount ? ` (${emailCount})` : ""}`}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="invite-result">
            <div className="grid grid-cols-2 gap-2 text-center">
              <div className="rounded-md p-3 bg-[#DCFCE7] border border-[#86EFAC]">
                <div className="text-2xl font-mono font-bold text-[#059669]">{result.added.length}</div>
                <div className="text-xs text-[#065F46] uppercase tracking-wider">
                  Added
                </div>
              </div>
              <div className="rounded-md p-3 bg-[#FEE2E2] border border-[#FCA5A5]">
                <div className="text-2xl font-mono font-bold text-[#DC2626]">{result.failed.length}</div>
                <div className="text-xs text-[#7F1D1D] uppercase tracking-wider">Failed</div>
              </div>
            </div>

            {result.added.length > 0 && (
              <div>
                <div className="text-sm font-medium text-[#0F172A] mb-2 flex items-center gap-1">
                  <CheckCircleIcon size={16} weight="fill" color="#059669" />
                  Added
                </div>
                <div className="border border-border rounded-md divide-y divide-border max-h-64 overflow-auto">
                  {result.added.map((i) => (
                    <div key={i.email} className="p-3 text-sm space-y-1 hover:bg-zinc-50">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-mono text-[#0F172A] truncate">{i.email}</div>
                        <div className="text-[10px] font-mono uppercase tracking-wider whitespace-nowrap">
                          <span className="text-[#94A3B8]">
                            {roleLabel(i.role)} · {i.emailSent ? "email sent" : "email failed"}
                          </span>
                        </div>
                      </div>
                      {!i.emailSent && (
                        <div className="text-xs bg-[#FFFBEB] border border-[#FDE68A] rounded p-2 text-[#92400E]">
                          The invite email didn't send ({i.emailError || "unknown reason"}). The account exists — tell them to sign in with their Microsoft account.
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.failed.length > 0 && (
              <div>
                <div className="text-sm font-medium text-[#0F172A] mb-2 flex items-center gap-1">
                  <XCircleIcon size={16} weight="fill" color="#94A3B8" /> Failed
                </div>
                <div className="border border-border rounded-md divide-y divide-border max-h-56 overflow-auto">
                  {result.failed.map((row) => (
                    <div key={row.email} className="p-3 text-sm space-y-1">
                      <div className="font-mono text-[#0F172A] truncate">{row.email}</div>
                      <div className="text-[11px] text-[#94A3B8] font-mono break-words">
                        {row.reason?.replace(/_/g, " ")}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={reset} data-testid="invite-more-btn">Add more</Button>
              <Button
                onClick={() => {
                  // Belt-and-suspenders alongside the onInvited call already
                  // fired right after submit() above — guarantees the Team
                  // table is current the moment this dialog closes, however
                  // it got here.
                  if (result?.added.length > 0 && onInvited) onInvited(result);
                  close();
                }}
                data-testid="invite-done-btn"
                className="bg-[#0176D3] hover:bg-[#005FB2] text-white"
              >Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
